/* =====================================================================
   THE PANEL ROOM — SAMPLE DATA (single source of truth)
   ---------------------------------------------------------------------
   Every name, question, score, quote and contact detail on this site
   is ILLUSTRATIVE SAMPLE CONTENT, hard-coded in this one file.

   To replace it later: edit this file and every page updates.
   Nothing on the site talks to a server, database or messaging API.
   ===================================================================== */

export const SITE = {
    name: "The Panel Room",
    tagline: "Realistic mock interview boards for UPSC, Banking and State PSC aspirants.",
    /* SAMPLE contact details — replace with real ones */
    contact: {
        email: "board@thepanelroom.in",
        whatsapp: "+91 8788924082",
        hours: "Monday to Saturday, 10:00 AM – 6:00 PM",
        response: "We reply within one working day.",
    },
};

export const TICKER_ITEMS = [
    "UPSC Civil Services · Personality Test",
    "Bank PO Interviews",
    "State PSC Interviews",
    "Panels of retired civil servants",
    "Senior bankers",
    "Psychologists",
    "Scored written reports",
];

/* ---------------- Interactive board-room preview (Home) ---------------- */

export const BOARD_TABS = [
    {
        id: "civil-services",
        label: "Civil Services (UPSC)",
        seats: [
            { id: "chair", name: "Chairperson", note: "Retired Secretary, leads the board" },
            { id: "polity", name: "Member · Polity", note: "Governance & administration" },
            { id: "economy", name: "Member · Economy", note: "Finance & current affairs" },
            { id: "psych", name: "Member · Psychology", note: "Composure, ethics & honesty" },
        ],
        questions: [
            { seat: "chair", text: "Tell us about your district — and the one problem you would fix there first." },
            { seat: "polity", text: "Should a civil servant be free to disagree with the minister in public?" },
            { seat: "economy", text: "How would you fund flood resilience in a small town with a thin budget?" },
            { seat: "psych", text: "Describe a decision you took under real pressure. What did it cost you?" },
            { seat: "chair", text: "Why should the country trust a young officer with its hardest posting?" },
        ],
    },
    {
        id: "banking",
        label: "Banking (PO)",
        seats: [
            { id: "chair", name: "Chairperson", note: "Former General Manager, chairs the panel" },
            { id: "credit", name: "Member · Credit", note: "Lending, risk & recovery" },
            { id: "awareness", name: "Member · Banking", note: "RBI, monetary policy & schemes" },
            { id: "hr", name: "Member · HR", note: "Attitude, honesty & customer handling" },
        ],
        questions: [
            { seat: "chair", text: "Why banking, after a degree in engineering — convince the panel." },
            { seat: "credit", text: "A borrower with a clean record asks for one more loan to repay the last. What do you do?" },
            { seat: "awareness", text: "How does a repo rate cut travel down to a villager's small loan?" },
            { seat: "hr", text: "An angry customer shouts at your counter. Walk us through your next five minutes." },
            { seat: "chair", text: "Where do banks fit in a country where most payments are already digital?" },
        ],
    },
    {
        id: "state-psc",
        label: "State PSC",
        seats: [
            { id: "chair", name: "Chairperson", note: "Former Chief Secretary, chairs the board" },
            { id: "admin", name: "Member · Administration", note: "District administration & law and order" },
            { id: "state", name: "Member · State Affairs", note: "State economy, schemes & geography" },
            { id: "psych", name: "Member · Psychology", note: "Judgment, balance & integrity" },
        ],
        questions: [
            { seat: "chair", text: "What ails your state's schools — and what would you do in your first year?" },
            { seat: "admin", text: "A protest blocks the highway for two days. As the sub-collector, what is your order?" },
            { seat: "state", text: "Which single scheme of your state government would you redesign, and how?" },
            { seat: "psych", text: "Your senior orders what you believe is wrong. That evening, what do you actually do?" },
            { seat: "chair", text: "Why does your state need you on its administration — in one honest sentence?" },
        ],
    },
];

/* ---------------- How it works (Home) ---------------- */

export const HOW_IT_WORKS = [
    {
        step: "01",
        title: "Choose your exam",
        text: "UPSC, Bank PO or State PSC. Each board is seated and paced to match the real interview it imitates.",
    },
    {
        step: "02",
        title: "Meet your panel",
        text: "Four members are confirmed for your slot — a chairperson, subject experts and a psychologist.",
    },
    {
        step: "03",
        title: "Face the board",
        text: "A 25–30 minute mock in interview conditions: formal seating, profile-based questions, follow-ups and pressure.",
    },
    {
        step: "04",
        title: "Walk out with a report",
        text: "Within 24 hours: scores out of 10 on five dimensions, and a written remark from the panel.",
    },
];

/* ---------------- Sample report (Home preview + Sample Report page) ---------------- */

export const REPORT = {
    candidate: "A. Sharma",
    exam: "UPSC Civil Services — Personality Test",
    board: "Board of 4 members",
    date: "Saturday, 10:00 AM", // SAMPLE
    scores: [
        { label: "Content knowledge", score: 7 },
        { label: "Clarity", score: 8 },
        { label: "Composure", score: 6 },
        { label: "Current affairs", score: 7 },
        { label: "Ethics & integrity", score: 9 },
    ],
    overall: 7.4,
    remark:
        "A composed, well-prepared performance. Openings must tighten — answer the question asked, in your first sentence. Current-affairs depth is good; push it towards implications rather than facts. Hold your ground when challenged: the board rewards steadiness more than speed.",
    signOff: "— The Panel",
    /* Jev analysis of the remark (primary focus, tone, actionability) */
    remarkTags: {
        primary_focus: "delivery",
        focus_confidence: 0.77,
        tone: "encouraging",
        actionable: 0.94,
    },
};

/* ---------------- Exams page ---------------- */

export const EXAMS = [
    {
        id: "upsc",
        shortName: "UPSC",
        name: "Civil Services — Personality Test",
        pattern: ["275 marks · no minimum cutoff", "25–30 minutes", "Board of 5, chaired by a UPSC Member", "Questions drawn from your DAF"],
        panel: "Retired civil servants and academicians, with a psychologist on the board. The chairperson leads from your detailed application form (DAF).",
        questions: [
            "Your hobby is field hockey. What does Indian hockey teach a district collector?",
            "You find a scheme you praised in your DAF failing on the ground. What do you say to the board?",
            "Is the transfer of officers every few months reform or punishment?",
        ],
    },
    {
        id: "bank-po",
        shortName: "Bank PO",
        name: "Probationary Officer Interview",
        pattern: ["100 marks (varies by bank)", "15–20 minutes", "Panel of 4–5 senior bankers", "Profile + banking awareness"],
        panel: "General Managers and HR heads from public sector banks, sometimes with a retired Deputy Governor in the chair.",
        questions: [
            "What is financial inclusion beyond opening zero-balance accounts?",
            "A good borrower defaults after a crop failure. Contract or compassion?",
            "Explain yesterday's repo decision as you would to a first-time borrower.",
        ],
    },
    {
        id: "state-psc",
        shortName: "State PSC",
        name: "State Civil Service Interview",
        pattern: ["Marks vary by state", "20–30 minutes", "Board of 3–5 members", "State affairs + home district"],
        panel: "Retired state civil servants and subject experts from the state, seated by a former Chief Secretary or High Court figure.",
        questions: [
            "Which boundary of your district would you redraw, and why?",
            "Your state's flagship scheme spends most on publicity. Defend or attack it.",
            "What does your state lose that its neighbours gain — and what will you do about it?",
        ],
    },
];

/* ---------------- Jev difficulty scores for the sample questions ----------------
   Scored once with Jev (jev-latest): ["easy","medium","hard"] per question, in order. */
export const QUESTION_DIFFICULTY = {
    upsc: ["medium", "medium", "medium"],
    "bank-po": ["medium", "medium", "hard"],
    "state-psc": ["medium", "medium", "hard"],
};

/* ---------------- Panel page ---------------- */

export const PANEL_FILTERS = ["All", "UPSC", "Banking", "State PSC"];

export const PANELISTS = [
    {
        id: "iyer",
        initials: "RI",
        name: "Smt. R. Iyer", // SAMPLE profile
        background: "Retired IAS officer and former Secretary, Government of India. Has chaired interview boards for state public service commissions for nearly a decade.",
        years: 34,
        exams: ["UPSC", "State PSC"],
    },
    {
        id: "menon",
        initials: "KM",
        name: "Shri K. Menon", // SAMPLE profile
        background: "Former General Manager with a public sector bank. Headed credit and risk desks across three zones, and now trains probationary officers before their boards.",
        years: 31,
        exams: ["Banking"],
    },
    {
        id: "fernandes",
        initials: "AF",
        name: "Dr. A. Fernandes", // SAMPLE profile
        background: "Organisational psychologist. Assesses composure and stress response on interview boards, and coaches candidates on how they are actually being read.",
        years: 18,
        exams: ["UPSC", "Banking", "State PSC"],
    },
    {
        id: "chauhan",
        initials: "DC",
        name: "Shri D. Chauhan", // SAMPLE profile
        background: "Retired IPS officer. Served on state PSC interview boards and is known for pressure-testing integrity questions without raising his voice.",
        years: 29,
        exams: ["UPSC", "State PSC"],
    },
];

/* ---------------- Book page ---------------- */

export const SLOTS = [
    "Saturday · 10:00 AM",
    "Saturday · 4:00 PM",
    "Sunday · 11:00 AM",
    "Sunday · 5:00 PM",
];
